import type { EditorAction } from "../../editor/reducers";
import type { Dispatch } from "react";
import type { TextRegionCreationTabFragment } from "../../client/generated";
import { ScrollArea } from "@acdc2/ui/components/scroll-area";
import { NavbarTabCloseButton } from "../../shell/NavbarTabCloseButton";
import { NavbarTabContent } from "../../shell/NavbarTabContent";
import { useTranslation } from "react-i18next";
import { makeTabIdentifier } from "../../editor/reducers";
import { useMemo } from "react";
import { assertTypeName } from "../../client/guards";
import { RegionUpdateForm } from "../RegionUpdateForm";
import { TextFormField } from "../../attributes/text/TextFormField";
import { useRichTextEditor } from "../../rich-text/useRichTextEditor";
import { Document } from "@tiptap/extension-document";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { Bold } from "@tiptap/extension-bold";
import { Italic } from "@tiptap/extension-italic";
import { Strike } from "@tiptap/extension-strike";
import { Underline } from "@tiptap/extension-underline";
import { Language } from "../../rich-text/extension-language";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import {
  useCreateTextRegionMutation,
  useTextRegionCreationTabSuspenseQuery,
} from "../../client/generated";

type Props = {
  fragment: TextRegionCreationTabFragment;
  dispatch: Dispatch<EditorAction>;
};

export function TextRegionCreationTab({ fragment, dispatch }: Props) {
  const { t } = useTranslation();

  return (
    <NavbarTabContent
      value={makeTabIdentifier("text", fragment.id)}
      title={t("TextRegionCreationTab.title")}
      right={<NavbarTabCloseButton dispatch={dispatch} />}
    >
      <ScrollArea className="flex-1">
        <Body fragment={fragment} dispatch={dispatch} />
      </ScrollArea>
    </NavbarTabContent>
  );
}

function Body({ fragment, dispatch }: Props) {
  const textAttributeIds = fragment.attributes
    .filter((attr) => attr.__typename === "TextAttribute")
    .map((attr) => attr.id);

  const { data } = useTextRegionCreationTabSuspenseQuery({
    variables: {
      regionId: fragment.id,
      textAttributeIds,
    },
  });

  assertTypeName(data.region?.__typename === "Region");

  const [createRegion] = useCreateTextRegionMutation();
  const regionText = useMemo(() => {
    const attr = data.textAttributes[0];
    if (!attr) return undefined;
    assertTypeName(attr.__typename === "TextAttribute");
    return attr.text;
  }, [data.textAttributes]);

  const editor = useRichTextEditor({
    options: {
      extensions: [
        Document,
        Paragraph,
        Text,
        Bold,
        Italic,
        Strike,
        Underline,
        Language,
        TextStyle,
        Color,
      ],
      content: regionText ?? "",
    },
  });

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!editor) return false;

    const result = await createRegion({
      variables: {
        regionId: fragment.id,
        text: editor.getHTML(),
        excessAttributeIds: fragment.attributes.map((attr) => attr.id),
      },
      update: (cache) => {
        for (const attr of fragment.attributes) {
          const id = cache.identify(attr);
          cache.evict({ id });
          cache.gc();
        }
      },
    });

    return (
      !result.errors &&
      !result.data?.createTextAttribute.userErrors?.length &&
      !result.data?.deleteAttributes.userErrors?.length
    );
  };

  return (
    <RegionUpdateForm onSubmit={onSubmit} dispatch={dispatch}>
      <TextFormField fragment={data.region} editor={editor} />
    </RegionUpdateForm>
  );
}
